import { animate } from '@lit-labs/motion';
import { localized } from '@weavedev/lit-i18next';
import { css, CSSResultGroup, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';

import {
  ColorStepDto,
  ColorVersionStepDto,
  PrintOptionPerPositionStepDto,
  PrintOptionStepDto,
  QuantityPerSizePerColorStepDto,
  QuantityPerVariantStepDto,
  QuantityStepDto,
  StepDto,
  VersionStepDto,
} from '@/api-sdk';
import {
  ConfiguratorContextType,
  ValidationConfiguratorContextType,
} from '@/context';
import { BREAKPOINT_LARGE_PX, BREAKPOINT_MEDIUM_PX } from '@/settings';
import { buttonStyles, rootVariablesStyles } from '@/styles';
import { ActionEvent } from '@/types';
import { updateValidationConfiguratorContext } from '@/utils';
import { ConfiguratorMixin } from './configurator-mixin';
import { goToStep, setScrollDelay } from './utils';

import '../loader/fg-loader';
import '../login/fg-login-cta';
import '../messages/fg-message-snack-bar';
import './fg-configurator-action-sections';
import './fg-configurator-actions';
import './fg-configurator-error';
import './mobile/fg-configurator-mobile-actions';
import './mobile/fg-configurator-mobile-done';
import './mobile/fg-configurator-mobile-header';
import './mobile/fg-configurator-mobile-navigation';
import './step/fg-configurator-step-color';
import './step/fg-configurator-step-color-version';
import './step/fg-configurator-step-print-option';
import './step/fg-configurator-step-production-speed';
import './step/fg-configurator-step-quantity';
import './step/fg-configurator-step-version';
import './step/wholesale/fg-configurator-print-option-per-position';
import './step/wholesale/fg-configurator-quantity-variants';

/**
 * @element fg-configurator
 */
@customElement('fg-configurator')
@localized()
export class FgConfigurator extends ConfiguratorMixin(LitElement) {
  renderStep(step: StepDto) {
    const { showValidationMessages } = this.validationConfiguratorContext;
    switch (step.type) {
      case 'color': {
        step = step as ColorStepDto;
        return html`
          <fg-configurator-step-color
            class="config-step"
            id=${step.type}
            .colorOptions=${step.colors}
            .selectMode=${step.selectMode}
            .selectModeSwitchQueryString=${step.selectModeSwitchQueryString}
            .validationMessage=${step.validationMessage}
            .showValidationMessage=${showValidationMessages[step.type]}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @default-color-selected=${this.onDefaultColorSelected}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @color-selected=${(event: CustomEvent) =>
              (this.selectedColor = event.detail)}
          ></fg-configurator-step-color>
        `;
      }
      case 'color_version': {
        step = step as ColorVersionStepDto;
        return html`
          <fg-configurator-step-color-version
            class="config-step"
            id=${step.type}
            .colorOptions=${step.colors}
            .versionOptions=${step.versions}
            .selectMode=${step.selectMode}
            .selectModeSwitchQueryString=${step.selectModeSwitchQueryString}
            .validationMessage=${step.validationMessage}
            .showValidationMessage=${showValidationMessages[step.type]}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @default-color-selected=${this.onDefaultColorSelected}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @color-selected=${(event: CustomEvent) =>
              (this.selectedColor = event.detail)}
          ></fg-configurator-step-color-version>
        `;
      }
      case 'version': {
        step = step as VersionStepDto;
        return html`
          <fg-configurator-step-version
            class="config-step"
            id=${step.type}
            .versions=${step.versions}
            .validationMessage=${step.validationMessage}
            .showValidationMessage=${showValidationMessages[step.type]}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
          ></fg-configurator-step-version>
        `;
      }
      case 'print_option': {
        step = step as PrintOptionStepDto;

        return html`
          <fg-configurator-step-print-option
            class="config-step"
            id=${step.type}
            .selectedOptions=${step.selectedOptions}
            .cancelQueryString=${step.cancelQueryString}
            .unprintedQueryString=${step.unprintedQueryString}
            .addPrintOptionQueryString=${step.addPrintOptionQueryString}
            .unprinted=${step.unprinted}
            .isLoading=${this.getGetConfiguratorPromise !== null}
            .printOptionFilter=${step.printOptionFilter}
            .validationMessage=${step.validationMessage}
            .showValidationMessage=${showValidationMessages[step.type]}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @configurator-context-update=${(
              event: CustomEvent<{ context: ConfiguratorContextType }>,
            ) => {
              this.configuratorContext = event.detail.context;
              this.requestUpdate();
            }}
            @validation-configurator-context-update=${(
              event: CustomEvent<{
                context: ValidationConfiguratorContextType;
              }>,
            ) => {
              this.validationConfiguratorContext = event.detail.context;
              this.requestUpdate();
            }}
          ></fg-configurator-step-print-option>
        `;
      }
      case 'quantity': {
        step = step as QuantityStepDto;
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${step.type}
            .queryString=${this.queryString}
            .quantityStep=${step.type}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_per_color':
      case 'quantity_per_size': {
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${step.type}
            .queryString=${this.queryString}
            .quantityStep=${step.type}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_per_size_per_color': {
        step = step as QuantityPerSizePerColorStepDto;
        return html`
          <fg-configurator-step-quantity
            class="config-step"
            id=${step.type}
            .colors=${step.colors}
            .queryString=${this.queryString}
            .quantityStep=${step.type}
            .scrollToStep=${this.scrollToValidationMessage(step.type)}
            @quantity-set=${(event: CustomEvent) => this.onQuantitySet(event)}
            @scrolled-into-view=${this.resetScrollValidationMessage}
            @quantity-validation=${this.validateQuantity}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-step-quantity>
        `;
      }
      case 'quantity_per_variant': {
        step = step as QuantityPerVariantStepDto;
        return html`
          <fg-configurator-quantity-variant
            part=${this.globalContext.layout}
            class="config-step"
            id=${step.type}
            .stepType=${step.type}
            .renderData=${step}
            .queryString=${this.queryString}
            @quantity-validation=${this.validateQuantity}
            @quantity-set=${this.onQuantitySet}
            @validate-variant-stock=${this.validateStockPerVariant}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-quantity-variant>
        `;
      }
      case 'print_option_per_position': {
        step = step as PrintOptionPerPositionStepDto;
        return html`
          <fg-configurator-print-option-per-position
            part=${this.globalContext.layout}
            class="config-step"
            id=${step.type}
            .stepType=${step.type}
            .printPositions=${step.positions}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
            @configurator-reset-update=${(event: CustomEvent<boolean>) => {
              this.resetConfigurator = event.detail;
            }}
          ></fg-configurator-print-option-per-position>
        `;
      }
      case 'production_speed': {
        if (!this.salable) {
          return nothing;
        }
        return html`
          <fg-configurator-step-production-speed
            part=${this.globalContext.layout}
            class="config-step"
            id=${step.type}
            .options=${step.options}
            @option-selected=${(event: CustomEvent) =>
              this.onOptionSelected(event, step.type)}
          ></fg-configurator-step-production-speed>
        `;
      }
    }
  }

  handleToSave(eventToSaveName: ActionEvent) {
    this.eventToSave = eventToSaveName;
  }

  render() {
    if (this.isLoading) {
      return html`
        <fg-loader type="configurator"></fg-loader>
      `;
    }

    if (this.error) {
      return html`
        <fg-configurator-error></fg-configurator-error>
      `;
    }

    const { layout, loggedIn } = this.globalContext;
    const { salable } = this.configuratorContext;
    const { showValidationSnackbar } = this.validationConfiguratorContext;
    const { isMobile } = this.mobileController;

    if (!this.steps.length) {
      return null;
    }

    return html`
      <fg-configurator-mobile-actions
        .queryString=${this.queryString}
        .configuration=${this.configuration}
        .quantityValidated=${this.quantityValidated}
        @reset-configurator=${this.resetConfiguration}
        @toggle-configurator=${this.toggleConfigurator}
        @set-mode=${this.setMode}
      ></fg-configurator-mobile-actions>
      <div
        class="wrapper ${classMap({
          show: this.toggleMobileConfigurator,
          hidden: !this.toggleMobileConfigurator,
          oneColumn: !salable && loggedIn === true,
        })}"
        part=${layout}
        ${animate({ properties: ['transform', 'opacity'] })}
        ${ref(this.configuratorWrapper)}
      >
        ${isMobile
          ? html`
              <fg-configurator-mobile-header
                part=${layout}
                @toggle-configurator=${this.toggleConfigurator}
                .currentStep=${this.activeStep}
                .selectedColor=${this.selectedColor}
                .stepType=${this.steps[this.activeStep]?.type}
              ></fg-configurator-mobile-header>
            `
          : null}
        <div class="configurator" tabindex="0" ${ref(this.configuratorElement)}>
          ${this.steps.map((step, index) => {
            const preventProductionSpeedRender =
              step.type === 'production_speed' &&
              !salable &&
              layout === 'wholesale';

            return html`
              ${this.renderStep(step)}
              ${index < this.steps.length - 1 &&
              layout === 'wholesale' &&
              !preventProductionSpeedRender
                ? html`
                    <span class="delimiter"></span>
                  `
                : null}
            `;
          })}
          ${isMobile && layout === 'retail'
            ? html`
                <fg-configurator-mobile-done
                  class="config-step"
                ></fg-configurator-mobile-done>
              `
            : null}
        </div>

        <fg-configurator-action-sections
          part=${layout}
          .currentStep=${this.activeStep}
          .steps=${this.steps}
        >
          ${!isMobile && layout === 'wholesale'
            ? html`
                <fg-login-cta
                  slot="configurator-actions"
                  part=${layout}
                ></fg-login-cta>
              `
            : nothing}
          <fg-configurator-mobile-navigation
            part=${layout}
            .currentStep=${layout === 'retail'
              ? this.activeStep
              : this.activeStep + 1}
            .debtor=${this.debtor}
            .country=${this.country}
            .steps=${this.steps}
            .validSteps=${this.validSteps}
            .quantityValidated=${this.quantityValidated}
            .configuration=${this.configuration}
            @prev-step=${() => {
              this.activeStep--;
              goToStep(
                this.configuratorElement.value,
                this.activeStep,
                setScrollDelay(this.steps[this.activeStep].type),
              );
            }}
            @next-step=${() => {
              this.activeStep++;
              goToStep(
                this.configuratorElement.value,
                this.activeStep,
                setScrollDelay(this.steps[this.activeStep]?.type || null),
              );
            }}
            @trigger-validation-messages=${this.toggleValidationLogic}
            slot="mobile-navigation"
          ></fg-configurator-mobile-navigation>
          <fg-configurator-actions
            slot="configurator-actions"
            .configuration=${this.configuration}
            .unprinted=${this.unprinted}
            .eventToSave=${this.eventToSave}
            .product=${this.product}
            .quantityValidated=${this.quantityValidated}
            .queryString=${this.queryString}
            @trigger-validation-messages=${this.toggleValidationLogic}
            @prev-step=${() => {
              this.activeStep--;
              goToStep(this.configuratorElement.value, this.activeStep);
            }}
          ></fg-configurator-actions>
        </fg-configurator-action-sections>
      </div>
      ${!this.isConfiguratorLoaded
        ? html`
            <fg-loader type="in-page"></fg-loader>
          `
        : null}
      <fg-message-snack-bar
        part=${layout}
        ?show=${Boolean(showValidationSnackbar)}
        .type=${'error'}
        message=${showValidationSnackbar || ''}
        @on-close=${() => {
          this.validationConfiguratorContext =
            updateValidationConfiguratorContext({
              validationConfiguratorContext: this.validationConfiguratorContext,
              showValidationSnackbar: false,
            });
        }}
      ></fg-message-snack-bar>
    `;
  }

  static styles: CSSResultGroup = [
    buttonStyles,
    rootVariablesStyles,
    css`
      .configurator {
        display: flex;
        flex-direction: column;
        gap: 40px;
      }

      .wrapper:has([part='wholesale']) .configurator {
        gap: 32px;
      }

      .configurator:focus {
        outline: none;
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .wrapper:has([part='wholesale']):not(.oneColumn) {
          display: grid;
          grid-template-columns: repeat(2, minmax(100px, 50%));
          width: 100%;
          gap: 24px;
        }

        .delimiter {
          display: block;
          width: 100%;
          height: 1px;
          background-color: var(--fg-gray--300, var(--fg-fallback-gray--300));
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        .wrapper {
          display: grid;
          grid-template-rows: max-content auto max-content;
          grid-template-columns: 100%;
          transform: translateY(100%);
          height: 100dvh;
          width: 100vw;
          position: fixed;
          overflow-y: hidden;
          box-sizing: border-box;
          opacity: 0;
          background-color: #fff;
          z-index: 30;
        }

        .wrapper.show {
          opacity: 1;
          transform: translateY(0);
          inset: 0;
        }

        .wrapper.settled {
          transform: unset;
        }

        .configurator {
          flex-direction: row;
          gap: 0;
          scroll-snap-type: x mandatory;
          position: relative;
          height: auto;
          align-items: flex-start;
          overflow: hidden;
          margin-bottom: 50px;
        }

        .config-step {
          min-width: 100%;
          scroll-snap-align: start;
          padding: 16px 16px 70px;
          box-sizing: border-box;
          height: fit-content;
          max-height: 100%;
          overflow-y: scroll;
        }

        .wrapper:has([part='wholesale']) .config-step {
          padding: 16px 16px 100px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_LARGE_PX}px) {
        .wrapper:has([part='wholesale']):not(.oneColumn) {
          grid-template-columns: auto minmax(300px, 30%);
        }
      }
    `,
  ];
}
